import { useEffect, useRef, useState } from 'react'
import styles from './UserPermissionsScreen.module.css'
import {
  AppLayout,
  Mtds,
  OwnerId,
  CheckIcon,
  ScreenHeader,
  ScreenLayout,
  TopBar,
  TrashIcon,
  toSaveErrorMessage,
  components,
  useAddUserPermissionsMutation,
  useDeleteUserPermissionMutation,
  useGetRegionerQuery,
  useGetUserPermissionsQuery,
  usePermissionUsersQuery
} from 'prove-common'
import { useQueryClient } from '@tanstack/react-query'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'

type Assignment = components['schemas']['AddUserPermission']
type UnitOption = Assignment & { value: string; label: string }
type User = components['schemas']['User']

const responsibilityNames: Record<string, string> = {
  ADMIN: 'Administrator',
  PROGRAM_MANAGER: 'Programeier',
  COORDINATOR: 'Koordinator'
}
const responsibilityColors = {
  ADMIN: 'warning',
  PROGRAM_MANAGER: 'info',
  COORDINATOR: 'success'
} as const
const permissionKey = ({ responsibility, externalId }: Assignment) =>
  `${responsibility}|${externalId ? (OwnerId.fromString(externalId)?.toString() ?? externalId) : ''}`
const permissionLabel = (
  permission: Assignment,
  options: { value: string; label: string }[]
) =>
  `${responsibilityNames[permission.responsibility] ?? permission.responsibility} — ${
    permission.responsibility !== 'COORDINATOR'
      ? 'Global'
      : (options.find(({ value }) => value === permission.externalId)?.label ??
        permission.externalId)
  }`

const usePermissionUnits = () => {
  const regions = useGetRegionerQuery()
  const allOptions = (regions.data ?? [])
    .map(({ id, navn }) => ({
      value: new OwnerId('region', id).toString(),
      label: navn
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'nb'))
  return {
    options: allOptions,
    isSuccess: regions.isSuccess,
    isLoading: regions.isLoading,
    isError: regions.isError
  }
}

const UserPermissionEditor = ({
  user,
  units,
  onSaved,
  onClose
}: {
  user: User
  units: ReturnType<typeof usePermissionUnits>
  onSaved: () => void
  onClose: () => void
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const responsibilityRef = useRef<HTMLSelectElement>(null)
  const [responsibility, setResponsibility] = useState<
    Assignment['responsibility'] | ''
  >('')
  const [division, setDivision] = useState('')
  const queryClient = useQueryClient()
  const permissions = useGetUserPermissionsQuery(user.id)
  const addPermissions = useAddUserPermissionsMutation()
  const deletePermission = useDeleteUserPermissionMutation()
  const [selected, setSelected] = useState<UnitOption[]>([])
  const [removedIds, setRemovedIds] = useState<number[]>([])
  const [feedback, setFeedback] = useState<string>()
  const [isPending, setIsPending] = useState(false)
  const existingIds = new Set((permissions.data ?? []).map(permissionKey))
  const newSelections = selected.filter(({ value }) => !existingIds.has(value))
  const removals = (permissions.data ?? []).filter(({ id }) =>
    removedIds.includes(id)
  )
  const draftPermissions = [
    ...(permissions.data ?? [])
      .filter(({ id }) => !removedIds.includes(id))
      .map(({ id, ...assignment }) => ({
        id,
        ...assignment,
        value: permissionKey(assignment),
        label: permissionLabel(assignment, units.options)
      })),
    ...newSelections.map((option) => ({ ...option, id: undefined }))
  ]
  const draftIds = new Set(draftPermissions.map(({ value }) => value))
  const hasChanges = newSelections.length > 0 || removals.length > 0
  const assignment: Assignment | undefined = responsibility
    ? {
        responsibility,
        externalId: responsibility === 'COORDINATOR' ? division : undefined
      }
    : undefined
  const alreadyAssigned =
    assignment != null && draftIds.has(permissionKey(assignment))
  const canAdd =
    assignment != null &&
    permissions.isSuccess &&
    !isPending &&
    !alreadyAssigned &&
    (responsibility !== 'COORDINATOR' ||
      (!units.isLoading &&
        !units.isError &&
        units.options.some(({ value }) => value === division)))
  const addToDraft = () => {
    if (!canAdd || !assignment) return
    const value = permissionKey(assignment)
    const existing = permissions.data?.find(
      (item) => permissionKey(item) === value
    )
    if (existing) setRemovedIds((ids) => ids.filter((id) => id !== existing.id))
    else
      setSelected((items) => [
        ...items,
        {
          ...assignment,
          value,
          label: permissionLabel(assignment, units.options)
        }
      ])
    if (responsibility === 'COORDINATOR') setDivision('')
  }

  const canSave =
    hasChanges &&
    !isPending &&
    permissions.isSuccess &&
    newSelections.every(
      ({ responsibility, externalId }) =>
        responsibility !== 'COORDINATOR' ||
        (units.isSuccess &&
          units.options.some(({ value }) => value === externalId))
    )
  useEffect(() => {
    const dialog = dialogRef.current
    if (dialog && !dialog.open) dialog.showModal()
  }, [])

  useEffect(() => {
    if (!permissions.isSuccess) return
    const frame = requestAnimationFrame(() => {
      responsibilityRef.current?.focus()
      responsibilityRef.current?.scrollIntoView({ block: 'nearest' })
    })
    return () => cancelAnimationFrame(frame)
  }, [permissions.isSuccess])

  const saveChanges = async () => {
    if (!hasChanges || isPending || !permissions.isSuccess) return
    setIsPending(true)
    setFeedback(undefined)
    let savedChanges = false
    try {
      // Add first: removing the current user's admin grant must not prevent earlier additions.
      if (newSelections.length) {
        await addPermissions.mutateAsync({
          userId: user.id,
          permissions: newSelections.map(({ responsibility, externalId }) => ({
            responsibility,
            externalId
          }))
        })
        savedChanges = true
      }
      for (const { id } of [...removals].sort(
        (a, b) =>
          Number(a.responsibility === 'ADMIN') -
          Number(b.responsibility === 'ADMIN')
      )) {
        await deletePermission.mutateAsync({
          userId: user.id,
          permissionId: id
        })
        savedChanges = true
      }
      onSaved()
      dialogRef.current?.close()
    } catch (error) {
      const message = toSaveErrorMessage(error, 'lagre tilgangene', {
        conflictMessage:
          'En tilgang finnes allerede eller kan ikke endres. Oppdater listen og prøv igjen.'
      })
      setFeedback(
        savedChanges ? `Noen endringer er lagret. ${message}` : message
      )
    } finally {
      setIsPending(false)
    }
  }

  return (
    <Mtds.Dialog
      id='user-permissions-dialog'
      ref={dialogRef}
      aria-labelledby='permission-dialog-title'
      onClose={onClose}
      onCancel={(event) => {
        if (isPending) event.preventDefault()
      }}
    >
      <Mtds.Button
        aria-label='Lukk'
        command='close'
        commandfor='user-permissions-dialog'
        disabled={isPending}
      />
      <Mtds.Grid data-gap='3'>
        <Mtds.Heading as='h2' data-size='md' id='permission-dialog-title'>
          Tilganger for {user.name || user.id}
        </Mtds.Heading>
        {user.employeeId && (
          <Mtds.Muted>Ansattnummer: {user.employeeId}</Mtds.Muted>
        )}
        {feedback && (
          <Mtds.Alert role='alert' data-color='danger'>
            {feedback}
          </Mtds.Alert>
        )}
        {permissions.isLoading && <p role='status'>Laster tilganger …</p>}
        {permissions.isError && (
          <Mtds.Alert data-color='danger' role='alert'>
            Kunne ikke hente tilgangene.
            <Mtds.Button
              onClick={() => void permissions.refetch()}
              data-variant='secondary'
            >
              Prøv igjen
            </Mtds.Button>
          </Mtds.Alert>
        )}
        {permissions.isSuccess && !draftPermissions.length && (
          <p>Ingen tilganger er tildelt denne brukeren.</p>
        )}
        {draftPermissions.map((permission) => {
          const label = permission.label
          return (
            <Mtds.Flex
              key={permission.value}
              data-align='center'
              data-justify='space-between'
            >
              <span title={permission.value}>{label}</span>
              <Mtds.Button
                data-color='danger'
                aria-label={`Fjern tilgang til ${label}`}
                disabled={isPending || permissions.isError}
                onClick={() => {
                  const id = permission.id
                  if (id !== undefined) setRemovedIds((ids) => [...ids, id])
                  else
                    setSelected((items) =>
                      items.filter(({ value }) => value !== permission.value)
                    )
                  responsibilityRef.current?.focus()
                }}
              >
                <TrashIcon />
                Fjern
              </Mtds.Button>
            </Mtds.Flex>
          )
        })}
        {removals.map((permission) => (
          <Mtds.Flex
            key={permission.id}
            data-align='center'
            data-justify='space-between'
          >
            <Mtds.Muted>
              <s>{permissionLabel(permission, units.options)}</s> — fjernes ved
              lagring
            </Mtds.Muted>
            <Mtds.Button
              disabled={isPending}
              aria-label={`Angre fjerning av ${permissionLabel(permission, units.options)}`}
              onClick={() =>
                setRemovedIds((ids) => ids.filter((id) => id !== permission.id))
              }
            >
              Angre
            </Mtds.Button>
          </Mtds.Flex>
        ))}
        {units.isLoading && <p role='status'>Laster organisasjonsenheter …</p>}
        {units.isError && (
          <Mtds.Alert data-color='danger' role='alert'>
            Kunne ikke hente organisasjonsenhetene.
            <Mtds.Button
              data-variant='secondary'
              onClick={() => {
                for (const key of ['regioner']) {
                  void queryClient.invalidateQueries({ queryKey: [key] })
                }
              }}
            >
              Prøv igjen
            </Mtds.Button>
          </Mtds.Alert>
        )}
        {permissions.isSuccess && (
          <form
            id='user-permissions-form'
            onSubmit={(event) => {
              event.preventDefault()
              void saveChanges()
            }}
          >
            <Mtds.Grid data-gap='3'>
              <Mtds.Field>
                <Mtds.Field.Label htmlFor='permission-responsibility'>
                  Ansvar
                </Mtds.Field.Label>
                <Mtds.Select
                  id='permission-responsibility'
                  ref={responsibilityRef}
                  value={responsibility}
                  disabled={isPending || !permissions.isSuccess}
                  onChange={(event) => {
                    setResponsibility(
                      event.target.value as Assignment['responsibility'] | ''
                    )
                    setDivision('')
                  }}
                >
                  <option value=''>Velg ansvar</option>
                  <option value='COORDINATOR'>Koordinator</option>
                  <option value='PROGRAM_MANAGER'>Programeier</option>
                  <option value='ADMIN'>Administrator</option>
                </Mtds.Select>
                <Mtds.Field.Description>
                  {!responsibility
                    ? 'Velg ansvar først. Deretter velger du divisjon hvis ansvaret krever det.'
                    : responsibility === 'ADMIN'
                      ? 'Gjelder hele løsningen. Ingen divisjon velges.'
                      : responsibility === 'PROGRAM_MANAGER'
                        ? 'Forvalter instrukser og planer i alle fagseksjoner og tilsynsdivisjoner. Ingen divisjon velges.'
                        : 'Fordeler divisjonens tildelte arbeid og forvalter divisjonslokale oppgaver.'}
                </Mtds.Field.Description>
              </Mtds.Field>
              {responsibility === 'COORDINATOR' && (
                <Mtds.Field>
                  <Mtds.Field.Label htmlFor='permission-division'>
                    Divisjon
                  </Mtds.Field.Label>
                  <Mtds.Select
                    id='permission-division'
                    value={division}
                    disabled={
                      isPending || !permissions.isSuccess || !units.isSuccess
                    }
                    onChange={(event) => setDivision(event.target.value)}
                  >
                    <option value=''>Velg divisjon</option>
                    {units.options.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Mtds.Select>
                </Mtds.Field>
              )}
              {alreadyAssigned && (
                <p role='status'>Dette ansvaret er allerede lagt til.</p>
              )}
              <Mtds.Button
                type='button'
                data-variant='secondary'
                onClick={addToDraft}
                disabled={!canAdd}
              >
                Legg til i listen
              </Mtds.Button>
              <Mtds.Muted>
                Endringene blir først lagret når du velger Lagre.
              </Mtds.Muted>
            </Mtds.Grid>
          </form>
        )}
      </Mtds.Grid>
      <Mtds.Flex className='mt-8'>
        <Mtds.Button
          disabled={isPending}
          onClick={() => dialogRef.current?.close()}
        >
          Avbryt
        </Mtds.Button>
        <Mtds.Button
          type='submit'
          form='user-permissions-form'
          data-variant='primary'
          disabled={!canSave}
          aria-busy={isPending}
        >
          <CheckIcon />
          Lagre
        </Mtds.Button>
      </Mtds.Flex>
    </Mtds.Dialog>
  )
}

export const UserPermissionsScreen = () => {
  const [searchText, setSearchText] = useState('')
  const [selectedUser, setSelectedUser] = useState<User>()
  const [saveMessage, setSaveMessage] = useState<string>()
  const searchRef = useRef<HTMLInputElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const users = usePermissionUsersQuery()
  const units = usePermissionUnits()
  const searchTerms = searchText
    .trim()
    .toLocaleLowerCase('nb')
    .split(/\s+/)
    .filter(Boolean)
  const usersWithPermissions = (users.data ?? []).filter(
    (user) => user.permissions?.length
  )
  const matchingUsers = (users.data ?? []).filter((user) => {
    const text = [user.name, user.id, user.employeeId, user.email]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('nb')
    return searchTerms.every((term) => text.includes(term))
  })
  const selectUser = (user: User, trigger: HTMLElement) => {
    returnFocusRef.current = trigger ?? null
    setSaveMessage(undefined)
    setSearchText('')
    setSelectedUser(user)
  }

  return (
    <AppLayout
      useMobileBar={false}
      sidebar={<SideBarLayout />}
      topBar={<TopBar logo={<AppLogo />} title='Brukertilganger' />}
    >
      <ScreenLayout header={<ScreenHeader title='Brukertilganger' />}>
        <Mtds.Card>
          <Mtds.Grid data-gap='4'>
            <p>
              Søk og velg en bruker for å tildele ansvar. Programansvarlige
              forvalter divisjonens programmer, koordinatorer fordeler
              divisjonens arbeid, og administratorer har global tilgang. En
              bruker kan ha flere ansvar.
            </p>
            <Mtds.Field>
              <Mtds.Field.Label>Søk etter bruker</Mtds.Field.Label>
              <Mtds.Field.Description>
                Navn, e-post, brukernavn eller ansattnummer. Velg en bruker fra
                forslagene.
              </Mtds.Field.Description>
              <Mtds.Field.Suggestion
                id='permission-users'
                selected={[]}
                onSelectedChange={(items) => {
                  const user = users.data?.find(
                    ({ id }) => id === items?.[0]?.value
                  )
                  const trigger = document.activeElement
                  if (user && trigger instanceof HTMLElement)
                    selectUser(user, trigger)
                }}
              >
                <Mtds.Input
                  ref={searchRef}
                  placeholder='Søk etter bruker'
                  value={searchText}
                  onInput={(event) => setSearchText(event.currentTarget.value)}
                  disabled={!users.isSuccess}
                />
                <del role='img' aria-label='Fjern tekst' />
                <Mtds.Field.Datalist data-nofilter>
                  {searchTerms.length > 0 &&
                    matchingUsers.slice(0, 50).map((user) => (
                      <Mtds.Field.Option
                        key={user.id}
                        value={user.id}
                        label={user.name || user.id}
                      >
                        {[
                          user.name || user.id,
                          user.email,
                          user.employeeId && `Ansattnummer: ${user.employeeId}`
                        ]
                          .filter(Boolean)
                          .join(' – ')}
                      </Mtds.Field.Option>
                    ))}
                </Mtds.Field.Datalist>
              </Mtds.Field.Suggestion>
            </Mtds.Field>
            {users.isSuccess &&
              searchTerms.length > 0 &&
              !matchingUsers.length && (
                <p role='status'>Ingen brukere funnet. Prøv et annet søk.</p>
              )}
            {searchTerms.length > 0 && matchingUsers.length > 50 && (
              <Mtds.Muted>
                Viser de første 50 treffene. Skriv mer for å avgrense søket.
              </Mtds.Muted>
            )}
            {users.isLoading && <p role='status'>Laster brukere …</p>}
            {users.isError && (
              <Mtds.Alert data-color='danger' role='alert'>
                Kunne ikke hente brukere.
                <Mtds.Button onClick={() => void users.refetch()}>
                  Prøv igjen
                </Mtds.Button>
              </Mtds.Alert>
            )}
          </Mtds.Grid>
        </Mtds.Card>
        {users.isSuccess && (
          <Mtds.Card as='section' aria-label='Tildelte tilganger'>
            <Mtds.Grid data-gap='3'>
              <Mtds.Heading as='h2' data-size='md'>
                Tildelte tilganger
              </Mtds.Heading>
              {saveMessage && (
                <Mtds.Alert role='status' data-color='success'>
                  {saveMessage}
                </Mtds.Alert>
              )}
              <p role='status'>
                {`${usersWithPermissions.length} ${usersWithPermissions.length === 1 ? 'bruker har' : 'brukere har'} tildelte tilganger.`}
              </p>
              {units.isLoading && <p role='status'>Laster gruppenavn …</p>}
              {units.isError && (
                <Mtds.Alert data-color='warning'>
                  Kunne ikke hente alle divisjonsnavn. Divisjons-ID vises der
                  navnet mangler.
                </Mtds.Alert>
              )}
              {usersWithPermissions.length > 0 && (
                <Mtds.Table
                  className={styles.table}
                  data-size='sm'
                  data-align='center'
                  data-mobile='stacked'
                  data-mobile-size='640'
                  data-mobile-source='self'
                  aria-label='Brukere og tilganger'
                >
                  <Mtds.Table.Thead>
                    <Mtds.Table.Tr>
                      <Mtds.Table.Th scope='col'>Bruker</Mtds.Table.Th>
                      <Mtds.Table.Th scope='col' className={styles.compact}>
                        <abbr title='Ansattnummer'>Ansattnr.</abbr>
                      </Mtds.Table.Th>
                      <Mtds.Table.Th scope='col'>Tilganger</Mtds.Table.Th>
                      <Mtds.Table.Th scope='col' className={styles.compact}>
                        Handling
                      </Mtds.Table.Th>
                    </Mtds.Table.Tr>
                  </Mtds.Table.Thead>
                  <Mtds.Table.Tbody>
                    {usersWithPermissions.map((user) => (
                      <Mtds.Table.Tr key={user.id}>
                        <Mtds.Table.Th
                          scope='row'
                          title={user.email || user.id}
                          className={styles.name}
                        >
                          {user.name || user.id}
                        </Mtds.Table.Th>
                        <Mtds.Table.Td className={styles.compact}>
                          {user.employeeId || '–'}
                        </Mtds.Table.Td>
                        <Mtds.Table.Td>
                          {user.permissions?.length ? (
                            <Mtds.Flex data-gap='2'>
                              {user.permissions.map(({ id, ...assignment }) => (
                                <Mtds.Tag
                                  className={styles.permission}
                                  key={id}
                                  data-color={
                                    responsibilityColors[
                                      assignment.responsibility
                                    ]
                                  }
                                  data-size='sm'
                                  data-icon='none'
                                  title={permissionLabel(
                                    assignment,
                                    units.options
                                  )}
                                >
                                  <span className={styles.permissionText}>
                                    {permissionLabel(assignment, units.options)}
                                  </span>
                                </Mtds.Tag>
                              ))}
                            </Mtds.Flex>
                          ) : (
                            <Mtds.Muted>Ingen tildelte tilganger</Mtds.Muted>
                          )}
                        </Mtds.Table.Td>
                        <Mtds.Table.Td className={styles.compact}>
                          <Mtds.Button
                            type='button'
                            data-variant='secondary'
                            data-analytics-name='Velg bruker'
                            aria-label={`Rediger tilganger for ${user.name || user.id}`}
                            aria-haspopup='dialog'
                            onClick={(event) =>
                              selectUser(user, event.currentTarget)
                            }
                          >
                            Rediger
                          </Mtds.Button>
                        </Mtds.Table.Td>
                      </Mtds.Table.Tr>
                    ))}
                  </Mtds.Table.Tbody>
                </Mtds.Table>
              )}
            </Mtds.Grid>
          </Mtds.Card>
        )}
        {selectedUser && (
          <UserPermissionEditor
            key={selectedUser.id}
            user={selectedUser}
            units={units}
            onSaved={() => setSaveMessage('Tilgangene er lagret.')}
            onClose={() => {
              setSelectedUser(undefined)
              const trigger = returnFocusRef.current
              if (trigger?.isConnected) trigger.focus()
              else searchRef.current?.focus()
            }}
          />
        )}
      </ScreenLayout>
    </AppLayout>
  )
}
